import { useEffect, useRef, useState } from "react";

/**
 * Types `text` out character-by-character the first time the element scrolls
 * into view. Returns { ref, shown, typing }. The element should render
 * `shown` plus a caret while `typing`. Reduced motion shows the full text.
 * Re-runs when `text` changes (e.g. language switch).
 */
const useTypewriterOnView = (text, { speed = 26, startDelay = 160 } = {}) => {
  const ref = useRef(null);
  const [shown, setShown] = useState("");
  const [typing, setTyping] = useState(false);

  useEffect(() => {
    const node = ref.current;
    const full = text || "";

    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (!node || reduce || typeof IntersectionObserver === "undefined") {
      setShown(full);
      setTyping(false);
      return undefined;
    }

    let started = false;
    let timer = 0;

    const type = () => {
      setTyping(true);
      let i = 0;
      const step = () => {
        i += 1;
        setShown(full.slice(0, i));
        if (i < full.length) {
          timer = window.setTimeout(step, speed);
        } else {
          setTyping(false);
        }
      };
      timer = window.setTimeout(step, startDelay);
    };

    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !started) {
          started = true;
          type();
          io.unobserve(node);
        }
      },
      { threshold: 0.4 }
    );
    io.observe(node);

    return () => {
      io.disconnect();
      window.clearTimeout(timer);
    };
  }, [text, speed, startDelay]);

  return { ref, shown, typing };
};

export default useTypewriterOnView;
